import { MembraneSrnfSchema as BackendSrnfMembraneSchema } from "../../../../../backend/models/membrane-srnf.model"
import { useQuery } from '@tanstack/react-query'
import { fetchMembrane, fetchMembranes, fetchMembranesSchema, Membrane, MembraneFilters, MembranePaths, MembraneSchema } from '../membranes-queries'
import { RoMembranePaths } from "../ro/ro-queries"
import { CommonPaths, ResourceSchema } from "../../omd"
import { cloneDeep } from "lodash"


export type SrnfMembrane = Membrane<BackendSrnfMembraneSchema>

export type SrnfMembranePaths = MembranePaths<SrnfMembrane>

export type SrnfMembraneAllowedPaths = CommonPaths<
  SrnfMembranePaths,
  'characterizationResults.characterizationData' |
  'characterizationResults.contactAngle' |
  'characterizationResults.totalThickness' |
  'filtrationResults.mwco' |
  'filtrationResults.permeance' |
  'filtrationResults.solventPermeance' |
  'modification' |
  'supportLayerChemistry' |
  'testConditions.filtrationMode' |
  'testConditions.hydraulicP' |
  'testConditions.solvent1' |
  'testConditions.solvent2' |
  'testConditions.temperature' |
  "type" |
  "chemistry" |
  "structure" |
  "report.publicationDate"
> 
| 'soluteChoice.solute.molecularWeight' 
| 'soluteChoice.solute.concentration' 
| 'soluteChoice.solute.category'
| 'soluteChoice.solute.name'

// All filters available to the user for SRNF Membranes
export const srnfMembranesAllowedPaths: SrnfMembraneAllowedPaths[] =
  [
    'characterizationResults.characterizationData',
    'characterizationResults.contactAngle',
    'characterizationResults.totalThickness',
    'filtrationResults.mwco',
    'filtrationResults.permeance',
    'filtrationResults.solventPermeance',
    'modification',
    'supportLayerChemistry',
    'testConditions.filtrationMode',
    'testConditions.hydraulicP',
    'testConditions.solvent1',
    'testConditions.solvent2',
    'testConditions.temperature',
    'type',
    "chemistry",
    "structure",
    'report.publicationDate',
    'soluteChoice.solute.molecularWeight',
    'soluteChoice.solute.concentration',
    'soluteChoice.solute.category',
    'soluteChoice.solute.name',
  ] as const

export type SrnfMembranesFilters = MembraneFilters<SrnfMembrane, SrnfMembraneAllowedPaths> & { 'filter'?: string }

export type SrnfMembraneSchema = MembraneSchema<SrnfMembraneAllowedPaths | 'soluteChoice.solute.molecularWeight'>

export type SrnfMembranesQueryKey = 'srnf-membranes'
export const queryKey: SrnfMembranesQueryKey = 'srnf-membranes'
export const srnfMembranesQueryPath = '/membranes-srnf'
export type SrnfMembranesSchemaQueryKey = 'srnf-membranes-schema'
export const schemaQueryKey: SrnfMembranesSchemaQueryKey = 'srnf-membranes-schema'
export const srnfMembranesSchemaQueryPath = srnfMembranesQueryPath + '/schema'

export const fetchSrnfMembranes = (params?: SrnfMembranesFilters) => {
  let newParams = cloneDeep(params)
  let filter = { $and: [] as object[] }
  if (newParams && 'soluteChoice.solute.molecularWeight' in newParams) {
    if (typeof newParams['soluteChoice.solute.molecularWeight'] === 'object') {
      if (newParams['soluteChoice.solute.molecularWeight']) {
        const min = 'min' in newParams['soluteChoice.solute.molecularWeight'] ? newParams['soluteChoice.solute.molecularWeight'].min : undefined
        const max = 'max' in newParams['soluteChoice.solute.molecularWeight'] ? newParams['soluteChoice.solute.molecularWeight'].max : undefined
        filter['$and'].push({
          $or: [...Array(15).keys()].map(i => ({ $and: [{ [`soluteChoice.solute${i + 1}.molecularWeight`]: { $gte: min, $lte: max } }] }))
        })
      }
    }
    delete newParams['soluteChoice.solute.molecularWeight']
  }
  if (newParams && 'soluteChoice.solute.concentration' in newParams) {
    if (typeof newParams['soluteChoice.solute.concentration'] === 'object') {
      if (newParams['soluteChoice.solute.concentration']) {
        const min = 'min' in newParams['soluteChoice.solute.concentration'] ? newParams['soluteChoice.solute.concentration'].min : undefined
        const max = 'max' in newParams['soluteChoice.solute.concentration'] ? newParams['soluteChoice.solute.concentration'].max : undefined
        filter['$and'].push({
          $or: [...Array(15).keys()].map(i => ({ $and: [{ [`soluteChoice.solute${i + 1}.concentration`]: { $gte: min, $lte: max } }] }))
        })
      }
    }
    delete newParams['soluteChoice.solute.concentration']
  }
  if (newParams && 'soluteChoice.solute.category' in newParams) {
    if (Array.isArray(newParams['soluteChoice.solute.category']) && newParams['soluteChoice.solute.category'].length > 0) {
      console.log(newParams['soluteChoice.solute.category'])
      filter['$and'].push({
        $or: [...Array(15).keys()].map(i => ({ $and: [{ [`soluteChoice.solute${i + 1}.category`]: newParams!['soluteChoice.solute.category'] }] }))
      })
    }
    delete newParams['soluteChoice.solute.name']
  }
  if (newParams && 'soluteChoice.solute.name' in newParams) {
    if (Array.isArray(newParams['soluteChoice.solute.name']) && newParams['soluteChoice.solute.name'].length > 0) {
      console.log(newParams['soluteChoice.solute.name'])
      filter['$and'].push({
        $or: [...Array(15).keys()].map(i => ({ $and: [{ [`soluteChoice.solute${i + 1}.name`]: newParams!['soluteChoice.solute.name'] }] }))
      })
    }
    delete newParams['soluteChoice.solute.name']
  }

  if (filter.$and.length > 0) {
    if (newParams === undefined) newParams = {}
    newParams['filter'] = JSON.stringify(filter)
  }
  return fetchMembranes<SrnfMembrane, SrnfMembranesFilters>('/membranes-srnf', newParams)
}
export const fetchSrnfMembranesSchema = async () => {
  const srnfMembranesSchema = await fetchMembranesSchema<SrnfMembraneSchema>('/membranes-srnf/schema')
  srnfMembranesSchema['soluteChoice.solute.molecularWeight'] = {
    type: 'Number',
    path: 'soluteChoice.solute.molecularWeight',
    key: 'molecularWeight',
    labels: { short: 'Solute MW', long: 'Molecular Weight of solute' },
    validations: {}
  }
  srnfMembranesSchema['soluteChoice.solute.concentration'] = {
    type: 'Number',
    path: 'soluteChoice.solute.concentration',
    key: 'concentration',
    labels: { short: 'Solute concentration', long: 'Concentration of solute' },
    validations: {}
  }
  srnfMembranesSchema['soluteChoice.solute.category'] = {
    type: 'String',
    path: 'soluteChoice.solute.category',
    key: 'category',
    labels: { short: 'Solute category', long: 'Category of solute' },
    validations: {
      //@ts-expect-error 'soluteChoice.solute1.category' is not in AllowedPaths
      enum: srnfMembranesSchema['soluteChoice.solute1.category'].validations.enum
    }
  }
  srnfMembranesSchema['soluteChoice.solute.name'] = {
    type: 'String',
    path: 'soluteChoice.solute.name',
    key: 'name',
    labels: { short: 'Solute name', long: 'Name of solute' },
    validations: {
      //@ts-expect-error 'soluteChoice.solute1.name' is not in AllowedPaths
      enum: srnfMembranesSchema['soluteChoice.solute1.name'].validations.enum
    }
  }
  return srnfMembranesSchema
}

export const fetchSrnfMembrane = (id: BackendSrnfMembraneSchema['_id']) => {
  return fetchMembrane<SrnfMembrane>(`/membranes-srnf/${id}`)
}

export const useSrnfMembranesQuery = (params?: SrnfMembranesFilters) => useQuery(
  [queryKey, params],
  () => fetchSrnfMembranes(params),
)

export const useSrnfMembraneQuery = (id: BackendSrnfMembraneSchema['_id']) => useQuery(
  ['srnf-membrane', [id]],
  () => fetchSrnfMembrane(id),
)